import { useState, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { useAuth } from "@/hooks/use-auth";
import { useProfileCompletion } from "@/hooks/use-profile-completion";
import { ProfilePromptModal } from "@/components/ProfilePromptModal";
import { SignInButton } from "@clerk/clerk-react";
import { useLocation, useParams } from "wouter";
import { BottomNav } from "@/components/BottomNav";
import {
  ArrowLeft,
  DollarSign,
  TrendingUp,
  TrendingDown,
  Trophy,
  Skull,
  Users,
  Flame,
  Target,
  Loader2,
  LogIn,
  Zap,
  Swords,
  Info,
  Layers,
} from "lucide-react";

interface FoeEntry {
  rivalryType: "individual" | "team";
  mySide: { personId: string; name: string }[];
  theirSide: { personId: string; name: string }[];
  roundsPlayed: number;
  record: { wins: number; losses: number; ties: number };
  netResult: number;
}

function buildTeamKey(mySide: { personId: string }[], theirSide: { personId: string }[]): string {
  const sideA = mySide.map(p => p.personId).sort().join(",");
  const sideB = theirSide.map(p => p.personId).sort().join(",");
  const sides = [sideA, sideB].sort();
  return sides.join("_vs_");
}

type TimeFilter = "month" | "ytd" | "year";

const FILTER_LABELS: Record<TimeFilter, string> = {
  month: "Month",
  ytd: "YTD",
  year: new Date().getFullYear().toString(),
};

interface StrokeBucket {
  wins: number;
  ties: number;
  losses: number;
  total: number;
  winRate: number;
  countedScores: number;
  countedScoreRate: number;
}

interface ProfileData {
  displayName: string;
  net: number;
  wins: number;
  losses: number;
  ties: number;
  roundsPlayed: number;
  pressWinPct: number;
  roi: number;
  bestStreak: number;
  styleTag: string;
  frequentFoes: {
    individual: FoeEntry[];
    team: FoeEntry[];
  };
  biggestWin: {
    amount: number;
    opponents: string[];
    courseName: string;
    date: string;
  } | null;
  biggestLoss: {
    amount: number;
    opponents: string[];
    courseName: string;
    date: string;
  } | null;
  strokePerformance: {
    strokeHoles: StrokeBucket;
    straightHoles: StrokeBucket;
  } | null;
  teamContribution: {
    strokeHoleCountedRate: number;
    straightHoleCountedRate: number;
    strokeHoleTotal: number;
    straightHoleTotal: number;
  } | null;
  stakesPerformance: {
    tiers: {
      tier: string;
      net: number;
      wins: number;
      losses: number;
      ties: number;
      roundsPlayed: number;
      roi: number;
    }[];
    tag: string | null;
  } | null;
}

function formatMoney(amount: number): string {
  const abs = Math.abs(amount);
  return `$${abs % 1 === 0 ? abs.toFixed(0) : abs.toFixed(2)}`;
}

function formatDate(dateStr: string): string {
  const d = new Date(dateStr);
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
}

function SkeletonBlock({ className }: { className?: string }) {
  return <div className={`animate-pulse bg-gray-100 rounded-xl ${className ?? "h-24"}`} />;
}

export default function PlayerProfile() {
  const { user, isAuthenticated, isLoading: authLoading } = useAuth();
  const [, navigate] = useLocation();
  const params = useParams<{ personId: string }>();
  const personId = params?.personId;
  const [timeFilter, setTimeFilter] = useState<TimeFilter>("month");
  const [showProfilePrompt, setShowProfilePrompt] = useState(false);
  const { shouldShowOnRoute, dismiss: dismissProfilePrompt } = useProfileCompletion();

  useEffect(() => {
    if (shouldShowOnRoute) setShowProfilePrompt(true);
  }, [shouldShowOnRoute]);

  const { data: profile, isLoading, isError, error } = useQuery<ProfileData>({
    queryKey: ["/api/ledger/player", personId, "profile", timeFilter],
    queryFn: async () => {
      const res = await fetch(`/api/ledger/player/${personId}/profile?filter=${timeFilter}`, {
        credentials: "include",
      });
      if (res.status === 403) throw new Error("unauthorized");
      if (!res.ok) throw new Error("Failed to load profile");
      return res.json();
    },
    enabled: !!personId && isAuthenticated,
    retry: false,
  });

  if (authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <Loader2 className="w-8 h-8 animate-spin text-[#064E3B]" />
      </div>
    );
  }

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-gray-50 px-6">
        <LogIn className="w-12 h-12 text-gray-300 mb-4" />
        <h2 className="text-lg font-bold text-gray-800 mb-2" data-testid="text-unauthorized">Sign in to view profiles</h2>
        <SignInButton mode="modal">
          <button
            className="mt-4 px-6 py-2.5 bg-[#064E3B] text-[#FACC15] rounded-lg font-semibold"
            data-testid="button-signin"
          >
            Sign In
          </button>
        </SignInButton>
        <BottomNav />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 pb-20">
      <header className="sticky top-0 z-40 bg-[#064E3B] text-white px-4 pb-3 flex items-center gap-3 safe-area-top">
        <button
          onClick={() => navigate("/ledger")}
          className="p-1 -ml-1 rounded-lg hover:bg-white/10"
          data-testid="button-back"
        >
          <ArrowLeft className="w-5 h-5" />
        </button>
        <h1 className="text-base font-bold truncate" data-testid="text-page-title">
          {profile?.displayName ?? "Player Profile"}
        </h1>
      </header>

      <div className="px-4 py-3 flex gap-2 bg-white border-b border-gray-100">
        {(Object.keys(FILTER_LABELS) as TimeFilter[]).map((f) => (
          <button
            key={f}
            onClick={() => setTimeFilter(f)}
            className={`px-4 py-1.5 rounded-full text-xs font-semibold transition-colors ${
              timeFilter === f
                ? "bg-[#064E3B] text-[#FACC15]"
                : "bg-gray-100 text-gray-600"
            }`}
            data-testid={`filter-${f}`}
          >
            {FILTER_LABELS[f]}
          </button>
        ))}
      </div>

      <div className="px-4 pt-4 space-y-4">
        {isLoading ? (
          <>
            <SkeletonBlock className="h-48" />
            <SkeletonBlock className="h-32" />
            <SkeletonBlock className="h-24" />
            <SkeletonBlock className="h-40" />
          </>
        ) : isError ? (
          <div className="text-center py-12" data-testid="text-error">
            <Skull className="w-10 h-10 mx-auto text-gray-300 mb-3" />
            <p className="text-sm font-semibold text-gray-600">
              {(error as Error)?.message === "unauthorized"
                ? "You don't have access to this player's profile"
                : "Something went wrong loading this profile"}
            </p>
            <button
              onClick={() => navigate("/ledger")}
              className="mt-4 text-sm text-[#064E3B] font-semibold underline"
              data-testid="button-error-back"
            >
              Back to Ledger
            </button>
          </div>
        ) : profile ? (
          <>
            <HeaderCard profile={profile} filter={timeFilter} />
            <FrequentFoesSection
              foes={profile.frequentFoes && 'individual' in profile.frequentFoes
                ? profile.frequentFoes
                : { individual: [], team: [] }}
              profilePersonId={personId!}
              callerPersonId={user?.personId ?? null}
            />
            <BiggestResultsSection biggestWin={profile.biggestWin} biggestLoss={profile.biggestLoss} />
            {profile.stakesPerformance && (
              <StakesPerformanceSection stakesPerformance={profile.stakesPerformance} />
            )}
            <StrokeUtilizationSection strokePerformance={profile.strokePerformance} />
            {profile.teamContribution && (
              <TeamContributionSection contribution={profile.teamContribution} />
            )}
          </>
        ) : (
          <div className="text-center py-12 text-gray-400" data-testid="text-no-data">
            No profile data available
          </div>
        )}
      </div>

      <BottomNav />

      <ProfilePromptModal
        open={showProfilePrompt}
        onDismiss={() => { dismissProfilePrompt(); setShowProfilePrompt(false); }}
      />
    </div>
  );
}

function HeaderCard({ profile, filter }: { profile: ProfileData; filter: TimeFilter }) {
  const netColor = profile.net > 0 ? "text-green-600" : profile.net < 0 ? "text-red-500" : "text-gray-500";
  const netPrefix = profile.net > 0 ? "+" : "";

  return (
    <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden" data-testid="card-header">
      <div className="bg-[#064E3B] px-5 py-4">
        <div className="flex items-center justify-between">
          <div>
            <div className="flex items-center gap-2">
              <h2 className="text-xl font-bold text-white" data-testid="text-player-name">{profile.displayName}</h2>
              <a href="/razzy-faq" className="text-emerald-300 active:text-[#FACC15]" data-testid="link-razzy-faq">
                <Info className="w-4 h-4" />
              </a>
            </div>
            {profile.styleTag && (
              <span className="text-[#FACC15] text-xs font-medium mt-0.5 inline-block" data-testid="text-style-tag">
                {profile.styleTag}
              </span>
            )}
          </div>
          <div className="text-right">
            <div className={`text-2xl font-bold ${profile.net >= 0 ? "text-[#FACC15]" : "text-red-300"}`} data-testid="text-net">
              {netPrefix}{formatMoney(profile.net)}
            </div>
            <div className="text-emerald-200 text-xs uppercase tracking-wide">
              {filter === "month" ? "This Month" : filter === "ytd" ? "Year to Date" : new Date().getFullYear()}
            </div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-4 divide-x divide-gray-100 px-1 py-3">
        <StatCell label="Record" value={`${profile.wins}-${profile.losses}`} subValue={profile.ties > 0 ? `${profile.ties}T` : undefined} icon={<Trophy className="w-3.5 h-3.5 text-[#064E3B]" />} testId="stat-record" />
        <StatCell label="Press Win" value={`${profile.pressWinPct}%`} icon={<Zap className="w-3.5 h-3.5 text-[#064E3B]" />} testId="stat-press" />
        <StatCell label="ROI" value={`${profile.roi > 0 ? "+" : ""}${profile.roi}%`} icon={<DollarSign className="w-3.5 h-3.5 text-[#064E3B]" />} testId="stat-roi" />
        <StatCell label="Best Streak" value={`${profile.bestStreak}W`} icon={<Flame className="w-3.5 h-3.5 text-orange-500" />} testId="stat-streak" />
      </div>

      <div className="px-5 pb-3">
        <div className="text-xs text-gray-500 text-center">
          {profile.roundsPlayed} round{profile.roundsPlayed !== 1 ? "s" : ""} played
        </div>
      </div>
    </div>
  );
}

function StatCell({ label, value, subValue, icon, testId }: {
  label: string;
  value: string;
  subValue?: string;
  icon: React.ReactNode;
  testId: string;
}) {
  return (
    <div className="flex flex-col items-center px-2 py-1" data-testid={testId}>
      {icon}
      <span className="text-sm font-bold text-gray-900 mt-1">{value}</span>
      {subValue && <span className="text-xs text-gray-500">{subValue}</span>}
      <span className="text-xs text-gray-500 mt-0.5">{label}</span>
    </div>
  );
}

function FrequentFoesSection({ foes, profilePersonId, callerPersonId }: { foes: ProfileData["frequentFoes"]; profilePersonId: string; callerPersonId: string | null }) {
  const [, navigate] = useLocation();
  const [activeTab, setActiveTab] = useState<"individual" | "team">("individual");
  const isOwnProfile = callerPersonId === profilePersonId;

  const currentList = activeTab === "individual" ? foes.individual : foes.team;
  const emptyMsg = activeTab === "individual"
    ? "No individual rivalries yet"
    : "No team rivalries yet — play more rounds with the same partners";

  return (
    <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-4" data-testid="section-frequent-foes">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Users className="w-4 h-4 text-[#064E3B]" />
          <h3 className="text-sm font-bold text-gray-900">Frequent Foes</h3>
        </div>
        <div className="flex bg-gray-100 rounded-lg p-0.5">
          <button
            onClick={() => setActiveTab("individual")}
            className={`text-xs font-semibold px-2.5 py-1 rounded-md transition-colors ${activeTab === "individual" ? "bg-white text-[#064E3B] shadow-sm" : "text-gray-400"}`}
            data-testid="tab-individual"
          >
            Individuals
          </button>
          <button
            onClick={() => setActiveTab("team")}
            className={`text-xs font-semibold px-2.5 py-1 rounded-md transition-colors ${activeTab === "team" ? "bg-white text-[#064E3B] shadow-sm" : "text-gray-400"}`}
            data-testid="tab-team"
          >
            Teams
          </button>
        </div>
      </div>

      {currentList.length === 0 ? (
        <p className="text-xs text-gray-500 text-center py-4" data-testid="text-no-foes">
          {emptyMsg}
        </p>
      ) : (
        <div className="space-y-2.5">
          {currentList.map((foe, i) => {
            const netColor = foe.netResult > 0 ? "text-green-600" : foe.netResult < 0 ? "text-red-500" : "text-gray-500";

            const theirLabel = foe.theirSide.map(p => p.name).join(" & ");
            const myLabel = foe.mySide.map(p => p.name).join(" & ");
            const displayLabel = foe.rivalryType === "team" ? `${myLabel} vs ${theirLabel}` : `vs ${theirLabel}`;

            const callerInSides = foe.mySide.some(p => p.personId === callerPersonId) || foe.theirSide.some(p => p.personId === callerPersonId);
            const canViewRivalry = isOwnProfile || callerInSides;

            let rivalryUrl: string;
            if (foe.rivalryType === "team") {
              rivalryUrl = `/rivalry/team/${buildTeamKey(foe.mySide, foe.theirSide)}`;
            } else {
              rivalryUrl = `/rivalry/player/${profilePersonId}/${foe.theirSide[0].personId}`;
            }

            const content = (
              <>
                <div className="flex items-center gap-2.5 min-w-0">
                  <div className="w-7 h-7 rounded-full bg-[#064E3B] flex items-center justify-center flex-shrink-0">
                    <span className="text-[#FACC15] text-xs font-bold">{i + 1}</span>
                  </div>
                  <div className="min-w-0 text-left">
                    <div className="text-sm font-semibold text-gray-900 truncate">{displayLabel}</div>
                    <div className="text-xs text-gray-500">
                      {foe.roundsPlayed} rounds · {foe.record.wins}W-{foe.record.losses}L{foe.record.ties > 0 ? `-${foe.record.ties}T` : ""}
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-1.5 flex-shrink-0">
                  <span className={`text-sm font-bold ${netColor}`}>
                    {foe.netResult > 0 ? "+" : ""}{formatMoney(foe.netResult)}
                  </span>
                  {canViewRivalry && <Swords className="w-3.5 h-3.5 text-gray-300" />}
                </div>
              </>
            );

            if (canViewRivalry) {
              return (
                <button
                  key={i}
                  onClick={() => navigate(rivalryUrl)}
                  className="w-full flex items-center justify-between active:bg-gray-50 rounded-lg py-1 px-0.5 transition-colors"
                  data-testid={`foe-row-${i}`}
                >
                  {content}
                </button>
              );
            }

            return (
              <div key={i} className="flex items-center justify-between py-1 px-0.5" data-testid={`foe-row-${i}`}>
                {content}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function BiggestResultsSection({
  biggestWin,
  biggestLoss,
}: {
  biggestWin: ProfileData["biggestWin"];
  biggestLoss: ProfileData["biggestLoss"];
}) {
  if (!biggestWin && !biggestLoss) {
    return (
      <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-4" data-testid="section-biggest-results">
        <h3 className="text-sm font-bold text-gray-900 mb-3">Highlights</h3>
        <p className="text-xs text-gray-500 text-center py-4" data-testid="text-no-highlights">
          No match results yet
        </p>
      </div>
    );
  }

  return (
    <div className="grid grid-cols-2 gap-3" data-testid="section-biggest-results">
      <ResultCard
        label="Biggest Win"
        icon={<Trophy className="w-4 h-4 text-green-600" />}
        data={biggestWin}
        colorClass="text-green-600"
        bgClass="bg-green-50"
        testId="card-biggest-win"
      />
      <ResultCard
        label="Biggest Loss"
        icon={<Skull className="w-4 h-4 text-red-500" />}
        data={biggestLoss}
        colorClass="text-red-500"
        bgClass="bg-red-50"
        testId="card-biggest-loss"
      />
    </div>
  );
}

function ResultCard({
  label,
  icon,
  data,
  colorClass,
  bgClass,
  testId,
}: {
  label: string;
  icon: React.ReactNode;
  data: ProfileData["biggestWin"] | null;
  colorClass: string;
  bgClass: string;
  testId: string;
}) {
  return (
    <div className={`rounded-2xl shadow-sm border border-gray-100 p-3.5 ${bgClass}`} data-testid={testId}>
      <div className="flex items-center gap-1.5 mb-2">
        {icon}
        <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">{label}</span>
      </div>
      {data ? (
        <>
          <div className={`text-lg font-bold ${colorClass}`} data-testid={`${testId}-amount`}>
            {data.amount > 0 ? "+" : ""}{formatMoney(data.amount)}
          </div>
          <div className="text-xs font-medium text-gray-700 mt-1 truncate" data-testid={`${testId}-opponents`}>
            vs {data.opponents.join(" & ")}
          </div>
          <div className="text-xs text-gray-500 mt-0.5 truncate">
            {data.courseName} · {formatDate(data.date)}
          </div>
        </>
      ) : (
        <div className="text-xs text-gray-500 mt-2">None yet</div>
      )}
    </div>
  );
}

const TIER_LABELS: Record<string, string> = {
  "$1-$9": "$1–9",
  "$10-$19": "$10–19",
  "$20-$49": "$20–49",
  "$50-$99": "$50–99",
  "$100+": "$100+",
};

function StakesPerformanceSection({ stakesPerformance }: { stakesPerformance: NonNullable<ProfileData["stakesPerformance"]> }) {
  return (
    <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-4" data-testid="section-stakes-performance">
      <div className="flex items-center gap-2 mb-3">
        <Layers className="w-4 h-4 text-[#064E3B]" />
        <h3 className="text-sm font-bold text-gray-900">Performance by Stakes</h3>
      </div>

      <div className="space-y-2">
        {stakesPerformance.tiers.map((t) => (
          <div
            key={t.tier}
            className="flex items-center justify-between py-2 px-3 rounded-xl bg-gray-50"
            data-testid={`stakes-tier-row-${t.tier}`}
          >
            <div className="flex items-center gap-3 min-w-0">
              <span className="text-xs font-bold text-gray-700 w-14 shrink-0">
                {TIER_LABELS[t.tier] ?? t.tier}
              </span>
              <span className="text-xs text-gray-500">
                {t.wins}-{t.losses}-{t.ties}
              </span>
              {t.roi !== 0 && (
                <span className={`text-xs font-medium ${t.roi > 0 ? "text-green-600" : "text-red-500"}`}>
                  ROI {t.roi > 0 ? "+" : ""}{t.roi.toFixed(0)}%
                </span>
              )}
            </div>
            <span className={`text-sm font-black shrink-0 ${
              t.net > 0 ? "text-green-600" : t.net < 0 ? "text-red-500" : "text-gray-400"
            }`}>
              {t.net > 0 ? "+" : ""}{formatMoney(t.net)}
            </span>
          </div>
        ))}
      </div>

      {stakesPerformance.tag && (
        <div className="mt-3 flex items-center gap-1.5" data-testid="stakes-tag">
          <Zap size={12} className="text-yellow-500" />
          <span className="text-[11px] font-bold text-gray-600">{stakesPerformance.tag}</span>
        </div>
      )}
    </div>
  );
}

function StrokeUtilizationSection({ strokePerformance }: { strokePerformance: ProfileData["strokePerformance"] }) {
  if (!strokePerformance) {
    return (
      <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-4" data-testid="section-stroke-util">
        <div className="flex items-center gap-2 mb-3">
          <Target className="w-4 h-4 text-[#064E3B]" />
          <h3 className="text-sm font-bold text-gray-900">Stroke Utilization</h3>
          <span className="text-xs bg-gray-100 text-gray-500 px-1.5 py-0.5 rounded font-medium">Lifetime</span>
        </div>
        <p className="text-xs text-gray-500 text-center py-4" data-testid="text-no-strokes">
          No hole data available yet
        </p>
      </div>
    );
  }

  const { strokeHoles, straightHoles } = strokePerformance;
  const delta = strokeHoles.total > 0 && straightHoles.total > 0
    ? (strokeHoles.winRate - straightHoles.winRate).toFixed(1)
    : null;

  return (
    <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-4" data-testid="section-stroke-util">
      <div className="flex items-center gap-2 mb-3">
        <Target className="w-4 h-4 text-[#064E3B]" />
        <h3 className="text-sm font-bold text-gray-900">Stroke Utilization</h3>
        <span className="text-xs bg-gray-100 text-gray-500 px-1.5 py-0.5 rounded font-medium">Lifetime</span>
      </div>

      <div className="space-y-3">
        <StrokeBucketRow
          label="With Strokes"
          bucket={strokeHoles}
          barColor="bg-[#064E3B]"
          testId="stroke-holes"
        />
        <StrokeBucketRow
          label="Straight Holes"
          bucket={straightHoles}
          barColor="bg-gray-400"
          testId="straight-holes"
        />
      </div>

      {delta !== null && (
        <div className="mt-3 pt-3 border-t border-gray-100 flex items-center justify-between">
          <span className="text-xs text-gray-500">Delta</span>
          <span
            className={`text-sm font-bold ${Number(delta) > 0 ? "text-green-600" : Number(delta) < 0 ? "text-red-500" : "text-gray-500"}`}
            data-testid="text-delta"
          >
            {Number(delta) > 0 ? "+" : ""}{delta}%
          </span>
        </div>
      )}
    </div>
  );
}

function StrokeBucketRow({ label, bucket, barColor, testId }: {
  label: string;
  bucket: StrokeBucket;
  barColor: string;
  testId: string;
}) {
  return (
    <div data-testid={testId}>
      <div className="flex items-center justify-between mb-1">
        <span className="text-xs font-medium text-gray-700">{label}</span>
        <span className="text-xs font-bold text-gray-900">{bucket.winRate}% win rate</span>
      </div>
      <div className="w-full h-2 bg-gray-100 rounded-full overflow-hidden mb-1">
        <div className={`h-full rounded-full ${barColor}`} style={{ width: `${Math.min(bucket.winRate, 100)}%` }} />
      </div>
      <div className="text-xs text-gray-500">
        {bucket.wins}W-{bucket.ties}T-{bucket.losses}L across {bucket.total} holes
      </div>
    </div>
  );
}

function TeamContributionSection({ contribution }: {
  contribution: NonNullable<ProfileData["teamContribution"]>;
}) {
  return (
    <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-4" data-testid="section-team-contribution">
      <div className="flex items-center gap-2 mb-3">
        <Users className="w-4 h-4 text-[#064E3B]" />
        <h3 className="text-sm font-bold text-gray-900">Team Contribution</h3>
        <span className="text-xs bg-gray-100 text-gray-500 px-1.5 py-0.5 rounded font-medium">Lifetime</span>
      </div>

      <div className="space-y-2.5">
        <div className="flex items-center justify-between" data-testid="team-stroke-counted">
          <div>
            <div className="text-xs font-medium text-gray-700">Counted Score Rate (Stroke Holes)</div>
            <div className="text-xs text-gray-500">{contribution.strokeHoleTotal} holes</div>
          </div>
          <span className="text-sm font-bold text-gray-900">{contribution.strokeHoleCountedRate}%</span>
        </div>
        <div className="flex items-center justify-between" data-testid="team-straight-counted">
          <div>
            <div className="text-xs font-medium text-gray-700">Counted Score Rate (Straight Holes)</div>
            <div className="text-xs text-gray-500">{contribution.straightHoleTotal} holes</div>
          </div>
          <span className="text-sm font-bold text-gray-900">{contribution.straightHoleCountedRate}%</span>
        </div>
      </div>
    </div>
  );
}
